import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import WorkspaceSetupOverlay from 'features/onboarding/components/WorkspaceSetupOverlay';
import preloadData, { areWorkspaceCachesReady } from 'shared/utils/preloadService';
import { fetchAndApplySubscriptionForDefaultTeam } from 'shared/utils/authSessionBootstrap';
import {
  clearWorkspaceSetupPending,
  isWorkspaceSetupPending,
  markWorkspaceSetupPending,
} from 'shared/utils/workspaceSetupPending';

const PUBLIC_PREFIXES = ['/login', '/signup', '/reset-password'];

function getSelectedTeamMongoId() {
  try {
    const raw = localStorage.getItem('selectedTeamData');
    if (!raw) return null;
    const t = JSON.parse(raw);
    return t?._id || t?.id || null;
  } catch {
    return null;
  }
}

// Routes where the overlay must never appear — either public auth pages, or
// authenticated pages that don't depend on preloaded analytics (settings, etc.).
function shouldSkipOverlay(pathname) {
  if (pathname === '/') return true;
  if (PUBLIC_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`))) return true;
  // Settings live at /:teamName/settings/* — overlay has nothing useful to show there.
  if (/\/settings(\/|$)/.test(pathname)) return true;
  if (/\/user-settings(\/|$)/.test(pathname)) return true;
  return false;
}

/**
 * After login redirect or team switch, keeps the same workspace setup overlay until
 * home workspace, events, agent event lists, and websites are cached for the active team.
 */
export default function WorkspaceSetupGate() {
  const location = useLocation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const publicPath = shouldSkipOverlay(location.pathname);
    const token = localStorage.getItem('accessToken');

    if (!token) {
      clearWorkspaceSetupPending();
      setVisible(false);
      return undefined;
    }

    if (publicPath) {
      setVisible(false);
      return undefined;
    }

    if (!isWorkspaceSetupPending()) {
      // Cold authenticated entry (returning user in a new window / fresh browser
      // session, where sessionStorage caches are empty). Give them the same
      // "loading… then you're in" experience as login: mark setup pending, warm
      // the caches login warms, and fall through to show the overlay until ready.
      // A warm same-tab refresh has caches ready here, so this stays silent.
      const teamName = localStorage.getItem('selectedTeam');
      const coldTeamMongoId = getSelectedTeamMongoId();
      if (teamName && !areWorkspaceCachesReady(coldTeamMongoId)) {
        markWorkspaceSetupPending();
        void preloadData(true, teamName);
        try {
          const raw = localStorage.getItem('selectedTeamData');
          if (raw) void fetchAndApplySubscriptionForDefaultTeam(JSON.parse(raw), 'return');
        } catch {
          /* ignore */
        }
      } else {
        setVisible(false);
        return undefined;
      }
    }

    setVisible(true);

    const tryFinish = () => {
      if (!isWorkspaceSetupPending()) {
        setVisible(false);
        return true;
      }
      const teamMongoId = getSelectedTeamMongoId();
      if (areWorkspaceCachesReady(teamMongoId)) {
        clearWorkspaceSetupPending();
        setVisible(false);
        return true;
      }
      return false;
    };

    if (tryFinish()) return undefined;

    const interval = setInterval(() => {
      if (tryFinish()) clearInterval(interval);
    }, 120);

    const timeout = setTimeout(() => {
      clearWorkspaceSetupPending();
      setVisible(false);
    }, 45000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [location.pathname]);

  useEffect(() => {
    const onTeamDataUpdated = () => {
      if (!isWorkspaceSetupPending()) return;
      const teamMongoId = getSelectedTeamMongoId();
      if (areWorkspaceCachesReady(teamMongoId)) {
        clearWorkspaceSetupPending();
        setVisible(false);
      }
    };
    window.addEventListener('teamDataUpdated', onTeamDataUpdated);
    return () => window.removeEventListener('teamDataUpdated', onTeamDataUpdated);
  }, []);

  if (!visible) return null;
  return <WorkspaceSetupOverlay />;
}
